import { useState } from 'react'
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useConnectionState,
  useLocalParticipant,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
} from '@livekit/components-react'
import { ConnectionState, Track, VideoPresets } from 'livekit-client'
import { LuMic, LuMicOff, LuScreenShare, LuScreenShareOff, LuVideo, LuVideoOff } from 'react-icons/lu'
import { ControlButton, StageNotice, StageSurface } from './streamStageChrome'

/**
 * The LiveKit half of a live stream's stage - one publisher, many watchers.
 *
 * Unlike the consultation room this is deliberately asymmetric. The host joins
 * with camera and mic; every viewer connects with `audio={false} video={false}`
 * and a token whose grant forbids publishing at all, so there is no path by
 * which an audience member appears on stage even if this component had a bug.
 *
 * Viewers render whatever the host is sending - their screen share when there
 * is one, their camera otherwise - and nothing else. No participant grid, no
 * roster: a broadcast has exactly one thing to look at.
 */
export default function LiveKitStreamStage({ session, isHost, onEnd, onLeft }) {
  return (
    <LiveKitRoom
      serverUrl={session.server_url}
      token={session.token}
      connect
      audio={isHost}
      video={isHost}
      options={{
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: VideoPresets.h720.resolution },
      }}
      onDisconnected={() => onLeft?.()}
      className="h-full w-full"
    >
      {/* The host's audio, played once at room level. A host never renders
          their own audio - that is a microphone fed into its own speakers. */}
      {!isHost && <RoomAudioRenderer />}
      {isHost ? <HostStage onEnd={onEnd} /> : <ViewerStage />}
    </LiveKitRoom>
  )
}

/** Where the host sees themselves and the controls that matter on air. */
function HostStage({ onEnd }) {
  const room = useRoomContext()
  const state = useConnectionState(room)
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant()
  const [error, setError] = useState(null)

  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], { onlySubscribed: false })
  const own = tracks.filter((t) => t.participant?.isLocal)
  const share = own.find((t) => t.source === Track.Source.ScreenShare)
  const camera = own.find((t) => t.source === Track.Source.Camera)
  const shown = share ?? camera

  async function toggleScreenShare() {
    try {
      await localParticipant.setScreenShareEnabled(!isScreenShareEnabled, { audio: true })
    } catch (err) {
      // Dismissing the browser's picker is a deliberate cancel, not a failure.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') setError(err?.message || 'Screen share failed.')
    }
  }

  return (
    <div className="flex h-full flex-col">
      <StageSurface>
        {shown ? (
          <VideoTrack trackRef={shown} className="h-full w-full object-contain" />
        ) : (
          <StageNotice
            title={state === ConnectionState.Connected ? 'Your camera is off' : 'Connecting…'}
            body={
              state === ConnectionState.Connected
                ? 'Viewers can still hear you. Turn the camera on when you are ready.'
                : 'Setting up your broadcast.'
            }
          />
        )}
      </StageSurface>

      {error && <p className="mt-2 text-[11px] text-red-300">{error}</p>}

      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <ControlButton
          active={isMicrophoneEnabled}
          onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled).catch(() => {})}
          onIcon={LuMic}
          offIcon={LuMicOff}
          label={isMicrophoneEnabled ? 'Mute' : 'Unmute'}
        />
        <ControlButton
          active={isCameraEnabled}
          onClick={() => localParticipant.setCameraEnabled(!isCameraEnabled).catch(() => {})}
          onIcon={LuVideo}
          offIcon={LuVideoOff}
          label={isCameraEnabled ? 'Camera off' : 'Camera on'}
        />
        <ControlButton
          active={isScreenShareEnabled}
          onClick={toggleScreenShare}
          onIcon={LuScreenShare}
          offIcon={LuScreenShareOff}
          label={isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}
        />
        <button
          type="button"
          onClick={onEnd}
          className="rounded-full bg-red-600 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[.15em] text-white transition-colors hover:bg-red-700"
        >
          End stream
        </button>
      </div>
    </div>
  )
}

/** What the audience sees: the host's share if there is one, else their camera. */
function ViewerStage() {
  const room = useRoomContext()
  const state = useConnectionState(room)
  const remotes = useRemoteParticipants()
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], { onlySubscribed: false })
  const remoteTracks = tracks.filter((t) => !t.participant?.isLocal && t.publication && !t.publication.isMuted)
  const shown =
    remoteTracks.find((t) => t.source === Track.Source.ScreenShare) ??
    remoteTracks.find((t) => t.source === Track.Source.Camera) ??
    null

  const connecting = state !== ConnectionState.Connected
  const hostPresent = remotes.length > 0

  return (
    <StageSurface>
      {shown ? (
        <VideoTrack trackRef={shown} className="h-full w-full object-contain" />
      ) : (
        <StageNotice
          title={connecting ? 'Joining…' : hostPresent ? 'Camera is off' : 'Waiting for the astrologer'}
          body={
            connecting
              ? 'Connecting you to the session.'
              : hostPresent
                ? 'You can still hear the session.'
                : 'The session is live — their video should appear in a moment.'
          }
        />
      )}
    </StageSurface>
  )
}
